/**
 * Darstellung des Reisetagebuchs.
 *
 * ## Warum hier nirgends `new Date(eintrag.ts)` steht
 *
 * Der Zeitstempel kommt als `2026-08-05T21:06:11` — ohne Zeitzone. Das ist
 * die **Wanduhrzeit**, zu der der Eintrag entstanden ist, und genau so soll
 * er dastehen: „21:06" heißt 21:06, egal wo man die Seite später öffnet.
 *
 * `new Date('2026-08-05T21:06:11')` würde die Zeichenkette als Ortszeit des
 * Browsers lesen und beim Formatieren womöglich verschieben. Wer im Winter
 * zu Hause die Bilder vom Gardasee durchsieht, bekäme 20:06 zu sehen — für
 * einen Abend, den er um 21:06 erlebt hat. Deshalb wird hier zerlegt, nicht
 * gerechnet.
 *
 * Dieselbe Überlegung steckt schon in `planner/tagesplan.ts`.
 */

/** Ein Medium am Eintrag. */
export interface Medium {
  id: number;
  art: 'foto' | 'video' | 'audio';
  original_name: string | null;
  mime: string | null;
  bytes: number | null;
  dauer_s: number | null;
  breite: number | null;
  hoehe: number | null;
  vorschau: string | null;
  /** Bildunterschrift — kurz, eine Zeile. */
  titel?: string | null;
  /** Was man wissen muss, um das Bild zu verstehen. */
  beschreibung?: string | null;
}

/** Eine Webadresse am Eintrag. */
export interface Verweis {
  id: number;
  url: string;
  titel: string | null;
  beschreibung: string | null;
  rechner?: string | null;
}

export interface Tagebucheintrag {
  id: number;
  ts: string;
  kind: string;
  title: string | null;
  note: string | null;
  lat: number | null;
  lon: number | null;
  place: string | null;
  soc: number | null;
  temperature_c: number | null;
  weather: string | null;
  odometer_km: number | null;
  actual_kwh: number | null;
  actual_km: number | null;
  tags: string[] | null;
  facts: Record<string, unknown> | null;
  shared: boolean;
  stop_id: number | null;
  medien: Medium[];
  links?: Verweis[];
  /**
   * Wer den Eintrag geschrieben hat — **nur bei Reisen mit Mitplanern**.
   *
   * Das Feld fehlt in der Antwort, solange nur ein Konto an der Reise hängt:
   * Dreißig Zeilen „Florian" untereinander unterscheiden nichts. Die
   * Oberfläche prüft deshalb auf „vorhanden" und braucht keine zweite Regel
   * dafür, wann sie den Namen zu verschweigen hat.
   *
   * `null` bei einem Eintrag aus der Zeit vor den Mitplanern — dort steht
   * keine Kennung, und den Besitzer einzusetzen wäre eine Vermutung, die man
   * später nicht mehr von einer Feststellung unterscheiden könnte.
   */
  verfasser_name?: string | null;
  /**
   * Was der Eintrag gekostet hat, in Euro.
   *
   * Kosten sind eine **Eigenschaft von Tagebucheinträgen** und keine eigene
   * Tabelle (Grundsatz 1 der Kostenbetrachtung): Ein Ist-Betrag ist ein
   * Eintrag mit Betrag, und damit bringt er Tag, Station, Land, Koordinate
   * und die Freigabe-Mechanik gratis mit.
   *
   * Alle fünf Felder sind optional, weil ein Server ohne die Spalten sie
   * nicht mitschickt — und `null`, solange der Eintrag nichts gekostet hat.
   * Die Auswertung steht in `lib/kosten.ts`, die Marke am Eintrag ebenso.
   */
  cost_eur?: number | null;
  /** Eine der neun Kategorien (`kosten.KATEGORIEN`). */
  cost_kind?: string | null;
  /** `beleg`, `diktat` oder `geschaetzt` — siehe `kosten.Kostenherkunft`. */
  cost_source?: string | null;
  /**
   * Der Betrag in der Währung, in der bezahlt wurde — nur bei Fremdwährung.
   *
   * Gerechnet wird immer in Euro (`cost_eur`), umgerechnet beim Erfassen und
   * nicht beim Auswerten. Diese beiden Felder halten fest, woher die Zahl
   * kommt: „18,40 € (16,50 CHF)" ist nachvollziehbar, „18,40 €" allein nicht.
   */
  cost_original?: number | null;
  cost_currency?: string | null;
  /**
   * Der Satz, der im Reisebuch als Zitat über dem Eintrag steht — oder `null`.
   *
   * Seit dem 7.10.2026 (Vertrag „Reisebuch als Magazin“, A4). Fehlt er, nimmt
   * das Buch den ersten Satz, sofern der Eintrag eine Überschrift hat; sonst
   * steht kein Zitat da. Optional, weil ein älterer Server das Feld nicht
   * mitschickt.
   */
  zitat?: string | null;
}

const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

/** `2026-08-05T21:06:11` → `2026-08-05`. Rein textlich. */
export function tagVon(ts: string): string {
  return (ts || '').slice(0, 10);
}

/** `2026-08-05T21:06:11` → `21:06`. Rein textlich. */
export function uhrzeitVon(ts: string): string {
  return (ts || '').slice(11, 16);
}

/**
 * `2026-08-05` → `Mittwoch, 5. August 2026`.
 *
 * Der Wochentag wird über einen UTC-Kalender bestimmt: Ein Datum ist hier ein
 * Kalendertag, kein Zeitpunkt. Mit Ortszeit läge `2026-08-05` je nach
 * Standort einen Tag daneben.
 */
export function tagUeberschrift(tag: string): string {
  const [j, m, t] = tag.split('-').map(Number);
  if (!j || !m || !t) return tag;
  const d = new Date(Date.UTC(j, m - 1, t));
  return `${WOCHENTAGE[d.getUTCDay()]}, ${t}. ${MONATE[m - 1]} ${j}`;
}

/** Einträge nach Kalendertagen bündeln — neueste zuerst, wie sie kommen. */
export function nachTagen(
  eintraege: Tagebucheintrag[]
): { tag: string; ueberschrift: string; eintraege: Tagebucheintrag[] }[] {
  const raus: { tag: string; ueberschrift: string; eintraege: Tagebucheintrag[] }[] = [];
  for (const e of eintraege) {
    const tag = tagVon(e.ts);
    const letzter = raus[raus.length - 1];
    if (letzter && letzter.tag === tag) letzter.eintraege.push(e);
    else raus.push({ tag, ueberschrift: tagUeberschrift(tag), eintraege: [e] });
  }
  return raus;
}

/**
 * Die Zeile unter der Überschrift: Ort, Ladestand, Temperatur.
 *
 * Was fehlt, fehlt — es wird nicht durch einen Platzhalter ersetzt. Ein
 * „– °C" sagt weniger als nichts: Es sieht aus wie ein Messfehler, dabei war
 * einfach keine Vorhersage da.
 */
export function umgebung(e: Tagebucheintrag): string {
  const teile: string[] = [];
  if (e.place) teile.push(e.place);
  if (e.temperature_c != null) teile.push(`${zahl(e.temperature_c)} °C`);
  if (e.weather) teile.push(e.weather);
  if (e.soc != null) teile.push(`${zahl(e.soc)} % Ladestand`);
  return teile.join(' · ');
}

function zahl(n: number): string {
  const gerundet = Math.round(n * 10) / 10;
  return String(gerundet).replace('.', ',');
}

/**
 * Einheiten, die am Ende eines Schlüssels stehen dürfen.
 *
 * `stellen` ist die Zahl der Nachkommastellen — und der eigentliche Grund für
 * diese Tabelle. Vorher wurde alles auf eine Stelle gerundet, und aus
 * `preis_eur_kwh: 0.39` wurde „0,4 Euro". Bei Geld ist die zweite Stelle
 * nicht Beiwerk, sondern die Aussage: 39 Cent gegen 40 Cent je Kilowattstunde
 * sind bei einer Ladung 60 Cent Unterschied.
 */
const EINHEITEN: Record<string, { zeichen: string; stellen: number; geld?: true }> = {
  eur: { zeichen: '€', stellen: 2, geld: true },
  cent: { zeichen: 'ct', stellen: 0 },
  c: { zeichen: '°C', stellen: 1 },
  km: { zeichen: 'km', stellen: 1 },
  m: { zeichen: 'm', stellen: 0 },
  kw: { zeichen: 'kW', stellen: 0 },
  kwh: { zeichen: 'kWh', stellen: 1 },
  watt: { zeichen: 'W', stellen: 0 },
  w: { zeichen: 'W', stellen: 0 },
  min: { zeichen: 'min', stellen: 0 },
  h: { zeichen: 'h', stellen: 1 },
  l: { zeichen: 'l', stellen: 1 },
  kg: { zeichen: 'kg', stellen: 1 },
  prozent: { zeichen: '%', stellen: 0 },
  pct: { zeichen: '%', stellen: 0 }
};
// `bar` steht bewusst **nicht** darin: Als letzter Bestandteil eines
// Schlüssels ist „bar" in diesem Projekt viel eher die Zahlungsart als der
// Druck — aus `preis_bar` würde sonst „12 bar" statt „Preis bar: 12".

/**
 * Wörter, die eine Umschrift oder Großschreibung brauchen.
 *
 * Ersetzt werden **ganze Wörter**, nie Teile davon. Ein `ue` → `ü` quer durch
 * die Zeichenkette wäre die naheliegende Abkürzung und wäre falsch: Aus
 * „neue" würde „nü". Eine Liste ganzer Wörter kann diesen Fehler nicht machen
 * — sie kennt eben nur, was sie kennt, und lässt alles andere in Ruhe.
 */
const WOERTER: Record<string, string> = {
  platzgebuehr: 'Platzgebühr',
  gebuehr: 'Gebühr',
  aussentemperatur: 'Außentemperatur',
  innentemperatur: 'Innentemperatur',
  sanitaer: 'Sanitär',
  mwst: 'MwSt',
  ust: 'USt',
  noetig: 'nötig',
  hoehe: 'Höhe',
  laenge: 'Länge',
  groesse: 'Größe',
  qualitaet: 'Qualität',
  naechte: 'Nächte',
  fruehstueck: 'Frühstück',
  oeffnung: 'Öffnung',
  schliessung: 'Schließung',
  duschen: 'Duschen',
  muecken: 'Mücken',
  wlan: 'WLAN',
  wc: 'WC',
  gps: 'GPS',
  soc: 'SoC',
  ac: 'AC',
  dc: 'DC',
  ph: 'pH'
};

/**
 * Nachkommastellen abschneiden und Nullen am Ende weglassen: `24.0` → `24`.
 *
 * Die Nullen fallen **nur hinter dem Komma** weg. Ein `/\.?0+$/` über die
 * ganze Zeichenkette war die naheliegende Abkürzung und machte aus 700 W
 * ein 7 W — es traf jede ganze Zahl, die auf Null endet.
 */
function nachkomma(n: number, stellen: number): string {
  let s = n.toFixed(stellen);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s.replace('.', ',');
}

/**
 * `{"preis_eur_kwh": 0.39}` → `Preis` · `0,39 €/kWh`.
 *
 * ## Was hier passiert
 *
 * Der Schlüssel wird von hinten gelesen: Solange die letzten Bestandteile
 * bekannte Einheiten sind, wandern sie in den Wert; der Rest ist die
 * Beschriftung. Deshalb wird aus `preis_eur_kwh` nicht „Preis eur kwh 0,4",
 * sondern „Preis 0,39 €/kWh" — und aus `nachtruhe_ab` bleibt „Nachtruhe ab",
 * weil `ab` keine Einheit ist.
 *
 * Was die Tabellen nicht kennen, bleibt unangetastet. Das ist Absicht: Die
 * freien Angaben sind ein Auffangbehälter, in den Cowork schreibt, was gerade
 * anfällt. Eine Aufbereitung, die rät, wäre schlimmer als eine, die nur das
 * anfasst, was sie sicher versteht.
 *
 * Nimmt das Objekt und nicht den Eintrag, weil die freien Angaben an zwei
 * Stellen stehen: intern als `facts`, in der öffentlichen Antwort als
 * `fakten`.
 */
export function faktenAus(
  f: Record<string, unknown> | null | undefined
): { was: string; wert: string }[] {
  return Object.entries(f ?? {}).map(([k, v]) => {
    const teile = k.split('_').filter(Boolean);
    // Einheiten von hinten abtragen — aber nie den letzten Bestandteil, sonst
    // stünde bei `{"km": 12}` eine Zahl ohne Beschriftung da.
    const einheiten: string[] = [];
    while (teile.length > 1 && EINHEITEN[teile[teile.length - 1].toLowerCase()]) {
      einheiten.unshift(teile.pop() as string);
    }

    const was = teile
      .map((t) => WOERTER[t.toLowerCase()] ?? t)
      .join(' ')
      .replace(/^./, (c) => c.toUpperCase());

    if (typeof v === 'boolean') return { was, wert: v ? 'ja' : 'nein' };
    if (typeof v !== 'number') return { was, wert: String(v) };

    if (!einheiten.length) return { was, wert: nachkomma(v, 2) };

    const gefunden = einheiten.map((e) => EINHEITEN[e.toLowerCase()]);
    // Geld steht vorn — egal, wie der Schlüssel geschrieben ist. Es kommt
    // beides vor (`preis_eur_kwh` und `preis_kwh_eur`), und „0,39 kWh/€"
    // steht auf dem Kopf: Gemeint sind Euro **je** Kilowattstunde, nicht
    // umgekehrt. Die Reihenfolge im Schlüssel darüber entscheiden zu lassen
    // hieße, die Aussage von einer Schreibweise abhängig zu machen.
    const sortiert = [...gefunden].sort((a, b) => Number(!!b.geld) - Number(!!a.geld));
    const zeichen = sortiert.map((e) => e.zeichen).join('/');

    // Bei Geld immer zwei Stellen, und die Nullen bleiben stehen: „12,00 €"
    // ist eine Preisangabe, „12 €" sieht aus wie eine gerundete Schätzung.
    // Sonst gibt die führende Einheit die Genauigkeit vor.
    const geld = sortiert.find((e) => e.geld);
    if (geld) return { was, wert: `${v.toFixed(2).replace('.', ',')} ${zeichen}` };
    return { was, wert: `${nachkomma(v, sortiert[0].stellen)} ${zeichen}` };
  });
}

export function faktenZeilen(e: Tagebucheintrag): { was: string; wert: string }[] {
  return faktenAus(e.facts);
}

/** `31.5` → `0:31`, `95` → `1:35`. Für Videos und Sprachnotizen. */
export function dauerText(sekunden: number | null): string | null {
  if (sekunden == null || sekunden <= 0) return null;
  const gesamt = Math.round(sekunden);
  const min = Math.floor(gesamt / 60);
  const sek = gesamt % 60;
  return `${min}:${String(sek).padStart(2, '0')}`;
}

/** `155353` → `152 KB`. Nur dort zeigen, wo die Größe eine Rolle spielt. */
export function groesse(bytes: number | null): string | null {
  if (bytes == null || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Eintragsorte für die Karte.
 *
 * Nur die mit Position: Ein Eintrag ohne Ort ist kein Fehler — er entstand
 * ohne Fahrzeugdaten und ohne Angabe — aber auf der Karte hätte er nichts
 * verloren.
 */
export function kartenpunkte(
  eintraege: Tagebucheintrag[]
): { id: number; lat: number; lon: number; text: string }[] {
  return eintraege
    .filter((e) => e.lat != null && e.lon != null)
    .map((e) => ({
      id: e.id,
      lat: e.lat as number,
      lon: e.lon as number,
      text: `${uhrzeitVon(e.ts)} — ${e.title || e.note || 'Eintrag'}`
    }));
}

/** Wie viele Medien welcher Art hängen an der Reise? Für die Kopfzeile. */
export function medienStand(eintraege: Tagebucheintrag[]): string | null {
  const zaehler = { foto: 0, video: 0, audio: 0 };
  for (const e of eintraege) for (const m of e.medien ?? []) zaehler[m.art]++;
  const teile: string[] = [];
  if (zaehler.foto) teile.push(`${zaehler.foto} Foto${zaehler.foto === 1 ? '' : 's'}`);
  if (zaehler.video) teile.push(`${zaehler.video} Video${zaehler.video === 1 ? '' : 's'}`);
  if (zaehler.audio)
    teile.push(`${zaehler.audio} Sprachnotiz${zaehler.audio === 1 ? '' : 'en'}`);
  return teile.length ? teile.join(' · ') : null;
}

/* ══ Das Zitat fürs Reisebuch ════════════════════════════════════════════ */

/**
 * Der erste Satz eines Textes — die Vorbelegung des Zitatfelds.
 *
 * Bis zum ersten Satzzeichen (`.`, `!`, `?`, `…`), auf das Leerraum oder das
 * Ende folgt, und höchstens bis zum Ende des ersten Absatzes. Ohne
 * Satzzeichen ist es der ganze erste Absatz. Abkürzungen wie „z. B.“ schneiden
 * zu früh ab; das ist hingenommen — das Feld ist ein Vorschlag, den man
 * ändert, und keine Entscheidung.
 */
export function ersterSatz(text: string | null | undefined): string {
  const absatz = (text ?? '').trim().split('\n')[0].trim();
  const treffer = absatz.match(/^(.+?[.!?…])(?=\s|$)/u);
  return (treffer ? treffer[1] : absatz).trim();
}

/** Was im Zitatfeld steht, wenn es aufgeht: das gesetzte Zitat, sonst der erste Satz. */
export function zitatVorbelegung(e: Pick<Tagebucheintrag, 'zitat' | 'note'>): string {
  return e.zitat?.trim() || ersterSatz(e.note);
}

/**
 * Was beim Speichern für `zitat` mitgeht — `undefined` heißt: gar nichts.
 *
 * Das Feld ist vorbelegt, und genau daran hängt die Regel. Wer es nicht
 * anfasst, markiert nichts: Sonst bekäme jeder Eintrag, dessen Text man
 * berichtigt, nebenbei ein Zitat — auch die ohne Überschrift, die nach der
 * Regel des Buchs gar keines tragen. Wer es anfasst, hat entschieden: Der
 * Text geht mit, ein leeres Feld als `null` (nimmt das Zitat weg). Und was
 * ohnehin schon gespeichert ist, wird nicht noch einmal geschickt.
 */
export function zitatAenderung(
  gespeichert: string | null | undefined,
  eingabe: string,
  beruehrt: boolean
): string | null | undefined {
  if (!beruehrt) return undefined;
  const neu = eingabe.trim();
  if (neu === (gespeichert ?? '').trim()) return undefined;
  return neu || null;
}
